<template>
  <main :aria-label="$t('evaluation_survey.list')" class="evaluation-survey-list-page">
    <CompanyBreadcrumb :aria-label="$t('evaluation_survey.list')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <template v-if="errorMessages && errorMessages.length">
      <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p>
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </template>

    <div v-if="successMessage" class="uk-alert-success" uk-alert>
      <a class="uk-alert-close" uk-close></a>
      <p>
        <i class="uil-check"></i> {{ successMessage }}
      </p>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle evaluation-survey-page-header">
      <div class="section-header-left">
        <h2 class="evaluation-survey-page-title">
          <span>{{ $t("evaluation_survey.list") }}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <router-link :to="{ name: 'company.evaluation-survey.add' }">
          <button :disabled="!$hasPermission('create_evaluationsurvey')"
                  :style="{ cursor: !$hasPermission('create_evaluationsurvey') ? 'not-allowed' : 'pointer' }"
                  aria-expanded="false"
                  class="btn btn-default evaluation-survey-add-btn"
                  type="button">
            <i class="uil-plus"></i> {{ $t("evaluation_survey.add") }}
          </button>
        </router-link>
      </div>
    </div>

    <div class="card position-relative evaluation-survey-list-card">
      <!-- Card header -->
      <div class="card-header actions-toolbar border-0 evaluation-survey-toolbar">
        <!-- Duz flex: mobilde arama tam satiri kaplayip sarmayi zorluyor,
             durum filtresi ile siralama alt satirda yan yana kaliyor. -->
        <div class="evaluation-survey-toolbar-inner">
          <!-- filter.search zaten watch ediliyor; @input gereksiz -->
          <CompanySearchInput v-model="filter.search"
                              :placeholder="$t('general.search')"
                              class="evaluation-survey-toolbar-search"
          />

          <div class="evaluation-survey-filter">
            <span class="evaluation-survey-filter-label">{{ $t('evaluation_survey.status') }}</span>
            <multiple-select-input v-model="selectedStatusOption"
                                   :allow-empty="false"
                                   :searchable="false"
                                   :show-labels="false"
                                   :values="statusOptions"
                                   class="evaluation-survey-filter-select"
            />
          </div>

          <div class="d-flex evaluation-survey-toolbar-actions">
            <sort-dropdown :sort-options="sortOptions" @sort-selected="onSortSelected"/>
          </div>
        </div>
      </div>

      <div v-if="loading"
           style="position: absolute; height: 100%; width: 100%; z-index: 999; background: rgba(238, 238, 238, 0.5); display: flex; justify-content: center; align-items: center">
        <div class="text-info" uk-spinner="ratio: 4"></div>
      </div>

      <!-- Table -->
      <div class="sf-table sf-table--zebra sf-table--hover evaluation-survey-table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">{{ $t("evaluation_survey.name") }}</th>
              <th scope="col">{{ $t("evaluation_survey.company") }}</th>
              <th scope="col">{{ $t("general.added_at") }}</th>
              <th scope="col">{{ $t("general.status") }}</th>
              <th class="sf-table__col-actions" scope="col"></th>
            </tr>
          </thead>
          <tbody class="list">
            <tr v-for="survey in surveys" :key="survey.id">
              <th scope="row">
                <div class="sf-table__media">
                  <template v-if="survey.is_default">
                    <span class="sf-table__name">{{ survey.name }}</span>
                    <i :uk-tooltip="$t('survey.default_description')"
                       aria-expanded="false"
                       class="uil-check-circle"
                       title=""></i>
                  </template>
                  <router-link v-else
                               :to="{
                                 name: 'company.evaluation-survey.edit',
                                 params: { id: survey.id },
                               }"
                               class="sf-table__name">
                    {{ survey.name }}
                  </router-link>
                </div>
              </th>
              <td>{{ survey.company_name }}</td>
              <td>{{ survey.created_at }}</td>
              <td :class="survey.is_active ? 'sf-table__status--success' : 'sf-table__status--danger'"
                  class="sf-table__status">
                {{ survey.is_active ? $t('evaluation_survey.active') : $t('evaluation_survey.inactive') }}
              </td>
              <td class="sf-table__col-actions">
                <!-- Actions -->
                <div class="sf-table__actions">
                  <a :uk-tooltip="$t('general.view')"
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     href="javascript:void(0)"
                     @click="showItem(survey)">
                    <i class="uil-eye"></i>
                  </a>

                  <router-link :to="{
                                 name: 'company.evaluation-survey.edit',
                                 params: { id: survey.id },
                               }"
                               :uk-tooltip="$t('general.edit')"
                               aria-expanded="false"
                               class="btn btn-icon btn-hover btn-sm btn-circle"
                               title="">
                    <i class="uil-edit"></i>
                  </router-link>

                  <a :uk-tooltip="$t('general.copy')"
                     aria-expanded="false"
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     href="javascript: void(0)"
                     @click="duplicate(survey.id)">
                    <i class="uil-copy text-info"></i>
                  </a>

                  <a :class="survey.is_default ? 'disabled' : ''"
                     :disabled="!$hasPermission('delete_evaluationsurvey')"
                     :style="{ cursor: !$hasPermission('delete_evaluationsurvey') ? 'not-allowed' : 'pointer' }"
                     :uk-tooltip="$t('general.delete')"
                     aria-expanded="false"
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     href="javascript:void(0)"
                     title=""
                     @click="removeItem(survey.id, survey.assignments_count)">
                    <i class="uil-trash text-danger"></i>
                  </a>
                </div>
              </td>
            </tr>

            <tr v-if="!loading && !surveys.length">
              <td class="sf-table__empty" colspan="5">{{ $t("general.no_records_found") }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <pagination v-if="pagination && pagination.hasOwnProperty('links')"
                  :pagination="pagination"
                  class="mt-2 mb-3 evaluation-survey-pagination"
                  @changePage="setPage($event)"/>
    </div>

    <AppModal :visible.sync="showPreviewModal" :title="survey_name" maxHeight="80vh" width="700px">
      <QuestionList :hideSubmit="true"
                    :modal_name="''"
                    :modal_title="survey_name"
                    :survey="survey_item"
                    @onClose="showPreviewModal = false"/>
    </AppModal>
  </main>
</template>
<script>
import Vue from "vue";
import store from "@/core/services";
import moment from "moment";
import module, {
  BASE_URL,
  GET_ITEMS,
  MODULE_NAME,
  ITEMS,
  ERROR,
  LOADING,
  DELETE_ITEM,
  DUPLICATE_ITEM
} from "@/core/services/store/evaluation_survey.module";
import Pagination from "@/assets/components/Pagination";
import QuestionList from "@/view/components/survey/QuestionList";
import Swal from "sweetalert2";
import AppModal from "@/view/components/ui/AppModal.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import CompanySearchInput from "@/view/company/components/search/CompanySearchInput.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";

export default {
  name: "EvaluationSurveysList",
  components: {
    AppModal,
    Pagination,
    QuestionList,
    CompanyBreadcrumb,
    CompanySearchInput,
    SortDropdown,
    MultipleSelectInput,
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
  },
  data() {
    return {
      //is_loading: false,
      pagination: null,
      activeItem: null,
      successMessage: null,
      errorMessages: [],
      page: 1,
      per_page: 10,
      filter: {
        search: null,
        is_active: null,
        company_id: null,
        order: "desc",
        sort: "id",
      },
      survey_item: {},
      survey_name: null,
      showPreviewModal: false,
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("survey.surveys") },
        { name: this.$t("evaluation_survey.surveys") },
      ];
    },
    statusOptions() {
      // trackBy "id" oldugu icin null id kullanilamaz; filtre degeri ayri tutuluyor
      return [
        { id: "all", value: null, name:this.$t("general.all")  },
        { id: "active", value: 1, name: this.$t("evaluation_survey.active") },
        { id: "inactive", value: 0, name: this.$t("evaluation_survey.inactive") },
      ];
    },
    // MultipleSelectInput nesne bekliyor, filter.is_active ise 1/0/null
    selectedStatusOption: {
      get() {
        return this.statusOptions.find((o) => o.value === this.filter.is_active)
            || this.statusOptions[0];
      },
      set(option) {
        this.filter.is_active = option ? option.value : null;
      },
    },
    sortOptions() {
      return [
        { key: "name-asc", sort: "name", order: "asc", label: this.$t("sorting.a_z") },
        { key: "name-desc", sort: "name", order: "desc", label: this.$t("sorting.z_a") },
        { key: "created_at-asc", sort: "created_at", order: "asc", label: this.$t("sorting.created_at_asc") },
        { key: "created_at-desc", sort: "created_at", order: "desc", label: this.$t("sorting.created_at_desc") },
      ];
    },
    loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) {
      },
    },
    items: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEMS];
      },
      set(value) {
      },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set(value) {
      },
    },
    // isLoading() {
    //   return this.loading
    // },
    surveys() {
      let results = [];
      if (this.items === null || !("data" in this.items)) return [];
      this.pagination = this.items;

      for (let i = 0; i < this.items.data.length; i++) {
        let currentItem = this.items.data[i];
        if (currentItem) {
          results.push({
            id: currentItem.id,
            name: currentItem.name,
            company_name: currentItem.company ? currentItem.company.name : '-',
            questions: currentItem.questions,
            is_default: currentItem.is_default,
            is_active: currentItem.is_active,
            assignments_count: currentItem.assignments_count,
            created_at: currentItem.created_at
              ? moment(currentItem.created_at).format("DD/MM/YYYY HH:mm:ss")
              : "-",
          });
        }
      }
      return results;
    },
  },
  methods: {
    deleteEvaluationSurvey(id) {
      this.$store
        .dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
          url: BASE_URL + "/" + id,
        })
        .then(() => {
          if (!this.error) {
            this.successMessage = this.$t("general.successfully_deleted");
            this.filterResult();
          } else this.errorMessages.push(this.error)
        });
    },
    removeItem(id, assignments_count) {
      let self = this;
      if (assignments_count) {
        Swal.fire({
          title: this.$t("general.sure_to_delete_survey_linked_to_education"),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: this.$t("general.yes"),
          cancelButtonText: this.$t("general.give_up"),
          confirmButtonColor: "#e31748",
        }).then((result) => {
          if (result.isConfirmed) {
            self.deleteEvaluationSurvey(id);
          }
        });
        return false;
      }

      Swal.fire({
        title: this.$t("general.sure_to_delete_survey"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("general.yes"),
        cancelButtonText: this.$t("general.give_up"),
        confirmButtonColor: "#e31748",
      }).then((result) => {
        if (result.isConfirmed) {
          self.deleteEvaluationSurvey(id);
        }
      });

    },
    showItem(survey) {
      this.survey_item = survey;
      this.survey_name = survey.name;
      this.showPreviewModal = true;
    },
    duplicate(id) {

      this.$store.dispatch(MODULE_NAME + "/" + DUPLICATE_ITEM, {
        url: BASE_URL + "/duplicate/" + id,
      }).then(() => {
        this.filterResult();
      });

    },
    filterResult() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: {
          search: this.filter.search,
          is_active: this.filter.is_active,
          company_id: this.company_id,
          page: this.page,
          per_page: this.per_page,
          order: this.filter.order,
          sort: this.filter.sort,
        },
      });
    },
    onSortSelected({ sort, order }) {
      this.sortOrder(sort, order);
    },
    sortOrder(sort, order) {
      let self = this;
      self.page = 1;
      self.filter.sort = sort;
      self.filter.order = order;
      this.filterResult();
    },
    setPage(page) {
      let self = this;
      self.page = page;
      this.filterResult();
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
  },
  watch: {
    "filter.search": function (val) {
      this.debouncedFilterSearch();
    },
    "filter.is_active": function (val) {
      this.debouncedFilterSearch();
    },
    "filter.company_id": function (val) {
      this.debouncedFilterSearch();
    },
    successMessage() {
      this.debouncedResetValues();
    },
    errorMessages() {
      this.debouncedResetValues();
    },
  },
  mounted() {
    this.filterResult();
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.filterResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
}
</script>
<style scoped>
/* Tablo tasarimi .sf-table'dan gelir (assets/scss/_data-table.scss).
   Burada yalnizca sayfaya ozgu degerler ayarlanir. */

.evaluation-survey-page-title {
  margin: 0;
}

.evaluation-survey-toolbar-inner {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  min-width: 0;
}

/* Arama sinirsiz buyumesin; filtre hemen saginda dursun */
.evaluation-survey-toolbar-search {
  flex: 0 1 320px;
  min-width: 180px;
  max-width: 320px;
}

/* Bos alan filtrenin onune birakiliyor; filtre ve siralama birlikte saga
   gidiyor. auto margin flex-grow'dan once uygulandigi icin bu yeterli. */
.evaluation-survey-filter {
  margin-left: auto;
}

.evaluation-survey-toolbar-actions {
  flex: 0 0 auto;
}

.evaluation-survey-filter {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.evaluation-survey-filter-label {
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

/* MultipleSelectInput koku .form-group; alt margin'i satiri sisiriyor */
.evaluation-survey-filter-select {
  --evaluation-filter-h: 38px;
  min-width: 180px;
  margin-bottom: 0;
}

.evaluation-survey-filter-select ::v-deep .multiselect,
.evaluation-survey-filter-select ::v-deep .multiselect__tags {
  box-sizing: border-box;
  height: var(--evaluation-filter-h);
  min-height: var(--evaluation-filter-h);
}

.evaluation-survey-filter-select ::v-deep .multiselect__tags {
  display: flex;
  align-items: center;
  padding: 0 32px 0 10px;
}

.evaluation-survey-filter-select ::v-deep .multiselect__single,
.evaluation-survey-filter-select ::v-deep .multiselect__placeholder,
.evaluation-survey-filter-select ::v-deep .multiselect__input {
  margin: 0;
  padding: 0;
  line-height: 1.4;
}

.evaluation-survey-filter-select ::v-deep .multiselect__select {
  top: 0;
  height: var(--evaluation-filter-h);
}

.evaluation-survey-pagination {
  padding: 0 8px 8px;
}

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

@media (max-width: 959px) {
  .evaluation-survey-page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .evaluation-survey-page-header .section-header-left,
  .evaluation-survey-page-header .section-header-right {
    width: 100%;
  }

  .evaluation-survey-page-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
  }

  .evaluation-survey-list-page .uk-alert p {
    font-size: 16px;
  }

  .evaluation-survey-page-header .section-header-right a {
    display: block;
  }

  .evaluation-survey-add-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  /* 1. satir: arama (tam genislik, sarmayi zorluyor)
     2. satir: durum filtresi + siralama */
  .evaluation-survey-toolbar-inner {
    flex-wrap: wrap;
    gap: 12px;
  }

  .evaluation-survey-toolbar-search {
    flex: 1 1 100%;
    max-width: none;
  }

  .evaluation-survey-filter {
    flex: 1 1 auto;
    /* masaustundeki saga itme mobilde gecersiz */
    margin-left: 0;
  }

  .evaluation-survey-toolbar-inner ::v-deep .company-search-input input {
    height: 48px;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
  }

  .evaluation-survey-filter-select {
    --evaluation-filter-h: 48px;
    width: 100%;
    min-width: 0;
  }

  /* 16px: iOS Safari daha kucuk font'ta odaklaninca sayfayi zoomluyor */
  .evaluation-survey-filter-select ::v-deep .multiselect__single,
  .evaluation-survey-filter-select ::v-deep .multiselect__placeholder {
    font-size: 16px;
  }

  .evaluation-survey-toolbar-actions {
    align-self: flex-end;
    justify-content: flex-end;
  }

  .evaluation-survey-toolbar-actions ::v-deep .btn-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }
}

@media (max-width: 767px) {
  .evaluation-survey-list-page {
    margin: 0;
    padding: 0 0 24px;
  }

  .evaluation-survey-list-card {
    border-radius: 14px;
  }

  .evaluation-survey-toolbar {
    padding: 16px;
  }

  .evaluation-survey-toolbar-actions {
    gap: 8px;
  }

  /* Tablo mobilde sikistirilmaz, yatay kaydirilir (5 kolon) */
  .evaluation-survey-table-wrap {
    --sf-table-min-width: 680px;
  }

  .evaluation-survey-pagination {
    padding: 0 16px 16px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .evaluation-survey-add-btn:hover:not(:disabled) {
    opacity: 0.92;
    cursor: pointer;
  }
}

@media (hover: none) {
  .evaluation-survey-list-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}
</style>
